import {
	exclamationTriangleIcon,
	eyeSlashIcon,
} from "../../../../core/elements/icons/index.js";
import CssClassNameVisitor from "../../../../models/reports/reports/CssClassNameVisitor.js";
import reportEditorSelection from "../../reportEditorSelection.js";
import ReportVisitor from "../../../../models/reports/reports/ReportVisitor.js";

/**
 * Abstract column renderer.
 *
 * @abstract
 * @param {report} spec.report - Report.
 */
export default class AbstractHtmlEditorColumnRenderer extends ReportVisitor {
	constructor({ report }) {
		super(...arguments);
		this._report = report;
		this._selection = reportEditorSelection.instance();
		this._cssClassNameRenderer = new CssClassNameVisitor();
	}

	visitGroupRow(row) {
		return (html) =>
			html.div(
				{
					klass: this._cssClassNameRenderer.visit(row),
					reportid: this._getRowId(row),
				},
				this._headerRowRenderer(row),
				(html) => {
					if (!row.isCollapsed()) {
						html.render(this._subRowsRenderer(row, row.getRows()));
					}
					if (this._report.getShowSummaryRows()) {
						html.render(this._summaryRowRenderer(row));
					}
				},
			);
	}

	visitRepeatingRow(row) {
		return (html) => {
			html.div(
				{
					klass: this._cssClassNameRenderer.visit(row),
					reportid: this._getRowId(row),
				},
				this._headerRowRenderer(row),
				this._subRowsRenderer(row, row.getOriginalRows()),
				this._repeatedRowsRenderer(row.getCloneRows()),
				this._rowRenderer(row, "repeating-end"),
			);
		};
	}

	visitGeneratedAccountGroupRow(row) {
		if (row.isExcluded()) {
			return this.visitAbstractRow(row);
		}
		return this.visitGroupRow(row);
	}

	_repeatedRowsRenderer(rows) {
		return (html) => html.div({ klass: "clones" }, this.visitAll(rows));
	}

	_headerRowRenderer(_row) {
		this._subclassResponsibility();
	}

	_subRowsRenderer(_row, _subrows) {
		this._subclassResponsibility();
	}

	_summaryRowRenderer(_row) {
		this._subclassResponsibility();
	}

	_iconRenderer(item) {
		return (html) => {
			const validation = item.validation();
			const valid = !validation.hasError();

			html.span({ klass: ["icon", { invalid: !valid }] }, (html) => {
				if (valid) {
					html.render(item.iconRenderer());
				} else {
					html.i({
						klass: exclamationTriangleIcon.getCssClass(),
						"data-toggle": "tooltip",
						"data-placement": "right",
						title: validation.getFirstDescription(),
					});
				}
			});
		};
	}

	_visibilityIconRenderer(item) {
		if (this._report.isItemVisible(item)) {
			return () => {};
		}

		return eyeSlashIcon({ class: "visibility-icon" });
	}

	_dimensionIconRenderer(item) {
		return (html) => {
			if (!item.hasOwnDimensionSpecification()) {
				return;
			}
			html.i({ klass: "fa fa-tag" });
		};
	}

	_titleCssClass(item) {
		return [
			this._selectableCssClass(item),
			this._selectedCssClass(item),
		].join(" ");
	}

	_selectableCssClass(item) {
		return item.canBeSelected() ? "selectable" : "";
	}

	_styleCssClass(item) {
		return item.getStyle();
	}

	_rowVisibilityCssClass(row) {
		return this._report.isRowVisible(row) ? "" : "row-invisible";
	}

	_selectedCssClass(item) {
		return item.canBeSelected() && this._selection.isSelected(item)
			? "selected"
			: "";
	}

	_getDepthCssClass(row) {
		return `data-depth-${row.getVisibilityDepth()}`;
	}

	_getRowId(row) {
		if (!row.getTemplate()) {
			return "";
		}
		return row.getTemplate().getId();
	}

	_toggleSelection(item) {
		if (!item.canBeSelected()) {
			return;
		}
		this._selection.toggle(item.getTemplate());
	}
}
